import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Field, TextInput } from "@odingard/design-system";
import { ApiError, type AppSumoTierSummary } from "@odingard/sdk-client";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

type Step = "account" | "org" | "codes" | "done";

// Split a pasted block into individual codes: one per line or whitespace-separated.
function parseCodes(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((c) => c.trim())
    .filter(Boolean);
}

export function Redeem() {
  const { user, organizations, register, login, refresh, setActiveOrg } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("account");
  const [authMode, setAuthMode] = useState<"register" | "login">("register");

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [orgChoice, setOrgChoice] = useState<string>("new");
  const [orgName, setOrgName] = useState("");
  const [orgId, setOrgId] = useState<string | null>(null);

  const [codesText, setCodesText] = useState("");
  const [summary, setSummary] = useState<AppSumoTierSummary | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Resume mid-flow: a signed-in buyer skips account creation.
  useEffect(() => {
    if (user && step === "account") setStep("org");
  }, [user, step]);

  useEffect(() => {
    const first = organizations[0];
    if (first && orgChoice === "new") {
      setOrgChoice(first.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations.length]);

  const parsed = useMemo(() => parseCodes(codesText), [codesText]);

  async function submitAccount(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (authMode === "register") {
        await register(email, password, displayName);
      } else {
        await login(email, password);
      }
      setStep("org");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign you in.");
    } finally {
      setBusy(false);
    }
  }

  async function submitOrg(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let resolvedOrgId: string;
      if (orgChoice === "new") {
        const org = await api.createOrganization(orgName);
        resolvedOrgId = org.id;
      } else {
        resolvedOrgId = orgChoice;
      }
      setActiveOrg(resolvedOrgId);
      setOrgId(resolvedOrgId);
      await refresh();
      setStep("codes");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not set up your organization.");
    } finally {
      setBusy(false);
    }
  }

  async function submitCodes(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    const codes = parsed;
    if (codes.length === 0) {
      setError("Enter at least one code.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    let lastSummary: AppSumoTierSummary | null = null;
    let alreadyCount = 0;
    try {
      // Redeem sequentially so each code stacks onto the same organization.
      for (const code of codes) {
        try {
          const res = await api.redeemAppSumo({ code, organizationId: orgId });
          lastSummary = res;
          if (res.alreadyRedeemed) alreadyCount += 1;
        } catch (err) {
          if (err instanceof ApiError && err.code === "code_already_redeemed") {
            alreadyCount += 1;
            continue;
          }
          throw err;
        }
      }
      if (!lastSummary) {
        lastSummary = await api.appsumoStatus(orgId).then((s) => ({
          redeemedCodes: s.redeemedCodes,
          tierCodes: s.tierCodes,
          maxCodes: s.maxCodes,
          seats: s.seats,
          monthlyPages: s.monthlyPages,
          entitlements: [],
        }));
      }
      setSummary(lastSummary);
      if (alreadyCount > 0) {
        setNotice(`${alreadyCount} code(s) were already redeemed and skipped.`);
      }
      await refresh();
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not redeem your codes.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <Link to="/" className="brand" style={{ justifyContent: "center", marginBottom: "var(--ods-space-5)" }}>
          <span className="brand__mark">O</span> Odingard Studio
        </Link>
        <Card>
          <CardBody>
            <h1 style={{ fontSize: "var(--ods-text-xl)", marginTop: 0 }}>Redeem your AppSumo codes</h1>
            <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginTop: 0 }}>
              Stack up to 4 codes on one organization. One code unlocks Contract AI; stacking adds the full Studio
              suite and higher limits.
            </p>

            {error && (
              <div className="banner banner--warning" role="alert">
                {error}
              </div>
            )}
            {notice && (
              <div className="banner banner--info" role="status">
                {notice}
              </div>
            )}

            {step === "account" && (
              <form onSubmit={submitAccount}>
                {authMode === "register" && (
                  <Field label="Full name">
                    {(id) => (
                      <TextInput id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
                    )}
                  </Field>
                )}
                <Field label="Work email">
                  {(id) => (
                    <TextInput
                      id={id}
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  )}
                </Field>
                <Field label="Password" help={authMode === "register" ? "At least 8 characters." : "Use your account password."}>
                  {(id) => (
                    <TextInput
                      id={id}
                      type="password"
                      autoComplete={authMode === "register" ? "new-password" : "current-password"}
                      minLength={authMode === "register" ? 8 : undefined}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  )}
                </Field>
                <Button type="submit" block disabled={busy}>
                  {busy ? "Please wait…" : authMode === "register" ? "Create account" : "Sign in"}
                </Button>
                <p style={{ textAlign: "center", fontSize: "var(--ods-text-sm)", marginTop: "var(--ods-space-3)" }}>
                  {authMode === "register" ? (
                    <>
                      Already have an account?{" "}
                      <button type="button" className="ods-linkbtn" onClick={() => setAuthMode("login")}>
                        Sign in
                      </button>
                    </>
                  ) : (
                    <>
                      New to Odingard?{" "}
                      <button type="button" className="ods-linkbtn" onClick={() => setAuthMode("register")}>
                        Create an account
                      </button>
                    </>
                  )}
                </p>
              </form>
            )}

            {step === "org" && (
              <form onSubmit={submitOrg}>
                {organizations.length > 0 && (
                  <Field label="Redeem into">
                    {(id) => (
                      <select
                        id={id}
                        className="ods-select"
                        value={orgChoice}
                        onChange={(e) => setOrgChoice(e.target.value)}
                      >
                        {organizations.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                        <option value="new">+ Create a new organization</option>
                      </select>
                    )}
                  </Field>
                )}
                {orgChoice === "new" && (
                  <Field label="Organization name">
                    {(id) => <TextInput id={id} value={orgName} onChange={(e) => setOrgName(e.target.value)} required />}
                  </Field>
                )}
                <Button type="submit" block disabled={busy}>
                  {busy ? "Setting up…" : "Continue"}
                </Button>
              </form>
            )}

            {step === "codes" && (
              <form onSubmit={submitCodes}>
                <Field label="Your codes" help="Paste one code per line, up to 4.">
                  {(id) => (
                    <textarea
                      id={id}
                      className="ods-textarea"
                      rows={4}
                      value={codesText}
                      onChange={(e) => setCodesText(e.target.value)}
                      placeholder={"AS-CONT-XXXXXXXX\nAS-CONT-YYYYYYYY"}
                      required
                    />
                  )}
                </Field>
                <p style={{ fontSize: "var(--ods-text-sm)", color: "var(--ods-color-muted)", marginTop: 0 }}>
                  {parsed.length} code{parsed.length === 1 ? "" : "s"} detected.
                </p>
                <Button type="submit" block disabled={busy || parsed.length === 0}>
                  {busy ? "Redeeming…" : `Redeem ${parsed.length || ""} code${parsed.length === 1 ? "" : "s"}`.trim()}
                </Button>
              </form>
            )}

            {step === "done" && summary && (
              <div>
                <div className="banner banner--info" role="status">
                  Success. {summary.redeemedCodes} code{summary.redeemedCodes === 1 ? "" : "s"} active on your
                  organization.
                </div>
                <ul style={{ fontSize: "var(--ods-text-sm)", lineHeight: 1.7 }}>
                  <li>
                    <strong>Tier:</strong> {summary.tierCodes} code{summary.tierCodes === 1 ? "" : "s"}
                  </li>
                  <li>
                    <strong>Seats:</strong> {summary.seats === null ? "Unlimited" : summary.seats}
                  </li>
                  <li>
                    <strong>Monthly pages:</strong>{" "}
                    {summary.monthlyPages === null ? "Unlimited" : summary.monthlyPages.toLocaleString()}
                  </li>
                </ul>
                <Button block onClick={() => navigate("/studio")}>
                  Enter Studio
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
        <p
          style={{
            textAlign: "center",
            fontSize: "var(--ods-text-sm)",
            color: "var(--ods-color-muted)",
            marginTop: "var(--ods-space-4)",
          }}
        >
          Need help? <Link to="/contact">Contact support</Link>
        </p>
      </div>
    </div>
  );
}
